import { debounce } from '@nova/utils'

/** Soft cap: products.json max 250/page × 4 pages. */
const COLLECTION_SEARCH_MAX_PAGES = 4
const COLLECTION_SEARCH_PAGE_SIZE = 250
/** Vendor/type pages use a Liquid view (paginate max 50). Soft cap stays 1000 products. */
const COLLECTION_SEARCH_VIEW = 'orp-products'
const COLLECTION_SEARCH_VIEW_PAGE_SIZE = 50
const COLLECTION_SEARCH_VIEW_MAX_PAGES = 20
/** Match product-card.liquid: never request full-size masters for grid cards. */
const COLLECTION_CARD_IMAGE_WIDTHS = [
  246, 320, 400, 480, 560, 640, 768, 900, 1024, 1200,
]
const COLLECTION_CARD_IMAGE_MAX_WIDTH = 1200
const COLLECTION_CARD_IMAGE_SIZES =
  '(max-width: 400px) 240px, (max-width: 480px) 320px, (max-width: 640px) 400px, (max-width: 767px) 480px, (max-width: 944px) 240px, (max-width: 1280px) 400px, (max-width: 1440px) 400px, (max-width: 1600px) 480px, (max-width: 1920px) 560px, (max-width: 144rem) 652px, 652px'

class NovaFiltersWrapper extends HTMLElement {
  constructor() {
    super()
    this.filterData = []
    this.debouncedOnSubmit = debounce(this.onSubmitHandler, 300)
    this.debouncedRenderPage = debounce(this.renderPage, 100).bind(this)
    this.debouncedDesktopFilterApply = debounce(
      this._applyDesktopFiltersFromForm,
      450,
    ).bind(this)
    this.debouncedCollectionSearch = debounce(
      this._onCollectionSearchInput,
      450,
    ).bind(this)
    this.templateType = this.dataset.type
    this.queryWord = this.dataset.query
    this.pendingController = null
    this.renderRequestToken = 0
    this.collectionSearchController = null
    this.collectionSearchToken = 0
  }

  connectedCallback() {
    this.$submitInputs = this.querySelectorAll('.js-filter-submit')
    this.$sortInput = this.querySelector('.js-sort-input')
    this.$collectionSearchInputs = this.querySelectorAll(
      '.js-collection-search-input',
    )
    this.$mobileFiltersTrigger = this.querySelector(
      '.js-mobile-filters-trigger',
    )
    this.$mobileFiltersDrawer = this.querySelector('.js-mobile-filters')
    this.$closeButton = this.querySelector('.js-close')
    this.$filterCheckboxes = this.querySelectorAll(
      '.js-collection-filters__form .js-filter-checkbox',
    )
    this.$summaryFilterCountEl = this.querySelectorAll('.js-filter-count')
    this.$totalFilterCountMarkers = document.querySelectorAll(
      '[data-total-filter-count]',
    )

    this.bindActiveFacetButtonEvents()
    window.addEventListener('popstate', this._onHistoryChange)
    document.body.addEventListener('click', this._closeDetails)

    document.addEventListener(
      'nova:filter-combobox:update',
      this._handleComboboxUpdate,
    )

    for (const $element of this.$submitInputs) {
      $element.addEventListener('click', this.debouncedOnSubmit)
    }
    this.$sortInput?.addEventListener('sl-change', this._onSortChange)
    this.$closeButton?.addEventListener('click', this._closeMobileFilters)

    for (const $checkbox of this.$filterCheckboxes) {
      $checkbox.addEventListener('sl-change', this._handleCheckboxChange)
    }

    this.$mobileFiltersTrigger?.addEventListener('click', () =>
      this.$mobileFiltersDrawer?.show(),
    )

    window.addEventListener('nova:filter-price-range:submit', (event) =>
      this._handlePriceRangeSubmit(event),
    )

    this._bindCollectionSearchInputs()

    if (this.classList.contains('js-desktop-filters')) {
      this._syncCustomTagFiltersToDesktop()
      this._initCollectionSearchFromUrl()
      this._initCustomTagOrFilteringFromPage()
    }
    this._fixDoubleQueryHrefs(document)
    this._hideNativeTagFilterIfCustomEnabled()
  }

  disconnectedCallback() {
    document.removeEventListener(
      'nova:filter-combobox:update',
      this._handleComboboxUpdate,
    )
    window.removeEventListener('popstate', this._onHistoryChange)
    document.body.removeEventListener('click', this._closeDetails)
  }

  _bindCollectionSearchInputs() {
    for (const input of this.$collectionSearchInputs || []) {
      if (input.dataset.collectionSearchBound === 'true') continue
      input.dataset.collectionSearchBound = 'true'
      input.addEventListener('sl-input', this.debouncedCollectionSearch)
      input.addEventListener('keydown', this._onCollectionSearchKeydown)
      input.addEventListener('sl-clear', this._onCollectionSearchClear)
    }
  }

  _onCollectionSearchKeydown = (event) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    this._runCollectionSearchFromInput(event.currentTarget)
  }

  _onCollectionSearchInput = (event) => {
    this._runCollectionSearchFromInput(event.currentTarget || event.target)
  }

  _onCollectionSearchClear = () => {
    this._syncCollectionSearchInputs('')
    this._clearCollectionSearchAndRestoreGrid()
  }

  _onSortChange = (event) => {
    const cq = this._getCollectionSearchQuery()
    if (cq) {
      event.preventDefault()
      this._runCollectionSearch(cq, true)
      return
    }
    this.debouncedOnSubmit(event)
  }

  _initCollectionSearchFromUrl() {
    if (this.templateType !== 'collection') return
    const cq = new URLSearchParams(window.location.search).get('cq')?.trim()
    if (!cq) return
    this._syncCollectionSearchInputs(cq)
    this._runCollectionSearch(cq, false)
  }

  _getCollectionSearchQuery() {
    const fromUrl = new URLSearchParams(window.location.search).get('cq')
    const inputs = document.querySelectorAll('.js-collection-search-input')
    for (const input of inputs) {
      const value = (input.value || '').trim()
      if (value) return value
    }
    return (fromUrl || '').trim()
  }

  _syncCollectionSearchInputs(value) {
    for (const input of document.querySelectorAll(
      '.js-collection-search-input',
    )) {
      input.value = value
    }
  }

  _runCollectionSearchFromInput(input) {
    const value = (input?.value || '').trim()
    this._syncCollectionSearchInputs(value)
    if (!value) {
      this._clearCollectionSearchAndRestoreGrid()
      return
    }
    this._runCollectionSearch(value, true)
  }

  _clearCollectionSearchInputs() {
    this._syncCollectionSearchInputs('')
  }

  _stripFacetParams(searchParams) {
    const next = new URLSearchParams()
    for (const [key, value] of searchParams.entries()) {
      if (key.startsWith('filter.')) continue
      if (key === 'page') continue
      if (key === 'cq') continue
      next.append(key, value)
    }
    return next
  }

  _clearCollectionSearchAndRestoreGrid() {
    const form =
      document.querySelector('.js-desktop-filters .js-collection-filters__form') ||
      document.querySelector('.js-collection-filters__form') ||
      document.querySelector('.js-mobile-filters-form')

    const params = this._ensureCollectionQueryParam(
      form
        ? new URLSearchParams(this._buildSearchParamsFromForm(form))
        : new URLSearchParams(window.location.search),
    )

    params.delete('cq')
    params.delete('page')

    const pathname = form
      ? this._getCollectionTagPath(form)
      : this._splitPathAndSearch(window.location.pathname).path

    this.renderPage(params.toString(), null, true, pathname)
  }

  async _runCollectionSearch(query, updateURLHash = true) {
    if (this.templateType !== 'collection') return
    if (!this.isConnected) return

    const trimmed = (query || '').trim()
    if (!trimmed) {
      this._clearCollectionSearchAndRestoreGrid()
      return
    }

    const collectionPath = this._getCollectionProductsBasePath()
    if (!collectionPath) return

    const requestToken = ++this.collectionSearchToken
    this.setLoadingState(true)

    try {
      const products = await this._fetchCollectionProducts(collectionPath)
      if (requestToken !== this.collectionSearchToken) return
      if (!this.isConnected) return

      const matched = this._filterProductsByQuery(products, trimmed)
      const sortBy =
        document.querySelector('.js-sort-input')?.value ||
        new URLSearchParams(window.location.search).get('sort_by') ||
        ''
      const sorted = this._sortCollectionProducts(matched, sortBy)

      this._renderCollectionSearchResults(sorted)

      if (updateURLHash) {
        const params = this._ensureCollectionQueryParam(
          this._stripFacetParams(new URLSearchParams(window.location.search)),
        )
        params.set('cq', trimmed)
        if (sortBy) params.set('sort_by', sortBy)
        const pathname =
          this._getCollectionMeta().path ||
          this._splitPathAndSearch(window.location.pathname).path
        this.updateURLHash(params.toString(), pathname)
        this._uncheckAllFacetCheckboxes()
        this._clearActiveFacetChips()
      }
    } catch (error) {
      if (error?.name === 'AbortError') return
      console.error('Collection search failed:', error)
    } finally {
      if (requestToken === this.collectionSearchToken) {
        this.setLoadingState(false)
      }
    }
  }

  _getCollectionMeta() {
    const root = document.getElementById('main-collection-filters')
    const rawUrl = root?.dataset?.collectionUrl || ''
    const pathOnly = (rawUrl.split('?')[0] || '').replace(/\/$/, '')
    const qFromData = (root?.dataset?.collectionQ || '').trim()
    const qFromLocation = new URLSearchParams(window.location.search)
      .get('q')
      ?.trim()
    const match = window.location.pathname.match(/^(\/collections\/[^/]+)/i)

    return {
      path: pathOnly || (match ? match[1] : ''),
      q: qFromData || qFromLocation || '',
    }
  }

  _getCollectionProductsBasePath() {
    return this._getCollectionMeta().path
  }

  _ensureCollectionQueryParam(searchParams) {
    const params =
      searchParams instanceof URLSearchParams
        ? searchParams
        : new URLSearchParams(searchParams || '')
    const { q } = this._getCollectionMeta()
    if (q && !params.get('q')) {
      params.set('q', q)
    }
    return params
  }

  _splitPathAndSearch(pathname = window.location.pathname) {
    const [pathOnly, query = ''] = String(pathname || '').split('?')
    return {
      path: pathOnly || window.location.pathname,
      searchParams: new URLSearchParams(query),
    }
  }

  async _fetchCollectionProducts(collectionPath) {
    if (this.collectionSearchController) {
      this.collectionSearchController.abort()
    }
    this.collectionSearchController = new AbortController()
    const { signal } = this.collectionSearchController
    const { q } = this._getCollectionMeta()

    // /collections/vendors|types?q=… are virtual pages; products.json is always empty.
    if (q) {
      return this._fetchCollectionProductsViaView(collectionPath, q, signal)
    }

    const allProducts = []

    for (let page = 1; page <= COLLECTION_SEARCH_MAX_PAGES; page++) {
      const params = new URLSearchParams({
        limit: String(COLLECTION_SEARCH_PAGE_SIZE),
        page: String(page),
      })
      const url = `${collectionPath}/products.json?${params.toString()}`
      const response = await fetch(url, { signal })
      if (!response.ok) {
        throw new Error(`Collection products fetch failed: ${response.status}`)
      }
      const data = await response.json()
      const products = Array.isArray(data.products) ? data.products : []
      allProducts.push(...products)
      if (products.length < COLLECTION_SEARCH_PAGE_SIZE) break
    }

    return allProducts
  }

  async _fetchCollectionProductsViaView(collectionPath, q, signal) {
    const allProducts = []

    for (let page = 1; page <= COLLECTION_SEARCH_VIEW_MAX_PAGES; page++) {
      const params = new URLSearchParams({
        view: COLLECTION_SEARCH_VIEW,
        page: String(page),
      })
      if (q) params.set('q', q)
      const url = `${collectionPath}?${params.toString()}`
      const response = await fetch(url, {
        signal,
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) {
        throw new Error(`Collection products view fetch failed: ${response.status}`)
      }
      const data = await response.json()
      const products = Array.isArray(data.products) ? data.products : []
      allProducts.push(...products)

      const totalPages = Number(data.pages) || 0
      if (totalPages && page >= totalPages) break
      if (products.length < COLLECTION_SEARCH_VIEW_PAGE_SIZE) break
    }

    return allProducts
  }

  _filterProductsByQuery(products, query) {
    const terms = query
      .toLowerCase()
      .split(/\s+/)
      .map((term) => term.trim())
      .filter(Boolean)

    if (!terms.length) return products

    const compactQuery = query.toLowerCase().replace(/[^a-z0-9]/g, '')

    return products.filter((product) => {
      const skus = Array.isArray(product.variants)
        ? product.variants.map((variant) => variant.sku).filter(Boolean)
        : []
      const haystack = [
        product.title,
        product.vendor,
        product.product_type,
        product.tags,
        ...skus,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      if (terms.every((term) => haystack.includes(term))) return true

      // Dashless SKU match: 3217102 finds variant sku 32-17-102
      if (compactQuery.length >= 4) {
        return skus.some(
          (sku) =>
            String(sku)
              .toLowerCase()
              .replace(/[^a-z0-9]/g, '')
              .includes(compactQuery),
        )
      }

      return false
    })
  }

  _sortCollectionProducts(products, sortBy) {
    const list = [...products]
    switch (sortBy) {
      case 'title-ascending':
        return list.sort((a, b) =>
          (a.title || '').localeCompare(b.title || ''),
        )
      case 'title-descending':
        return list.sort((a, b) =>
          (b.title || '').localeCompare(a.title || ''),
        )
      case 'price-ascending':
        return list.sort(
          (a, b) => this._productPriceNumber(a) - this._productPriceNumber(b),
        )
      case 'price-descending':
        return list.sort(
          (a, b) => this._productPriceNumber(b) - this._productPriceNumber(a),
        )
      case 'created-ascending':
        return list.sort(
          (a, b) =>
            Date.parse(a.created_at || 0) - Date.parse(b.created_at || 0),
        )
      case 'created-descending':
        return list.sort(
          (a, b) =>
            Date.parse(b.created_at || 0) - Date.parse(a.created_at || 0),
        )
      default:
        return list
    }
  }

  _productPriceNumber(product) {
    const variant = product.variants?.[0]
    const raw = variant?.price ?? product.price ?? '0'
    return Number.parseFloat(String(raw).replace(/,/g, '')) || 0
  }

  _normalizeVendor(vendor) {
    return String(vendor || '')
      .replace('Iceberg Outdoor Group - Supplier', '23Zero')
      .replace('Equipment Company', '')
      .replace('Dealers of ', '')
      .replace('Dealer Portal', '')
      .trim()
  }

  _priceToCentsAttr(value) {
    if (value === null || value === undefined || value === '') return ''
    const asString = String(value)
    if (asString.includes('.')) {
      return asString.replace('.', '')
    }
    const n = Number.parseFloat(asString.replace(/,/g, ''))
    if (!Number.isFinite(n)) return ''
    return String(Math.round(n * 100))
  }

  _shopifyImageUrl(url, width) {
    if (!url) return ''
    try {
      const base = new URL(
        url.startsWith('//') ? `https:${url}` : url,
        window.location.origin,
      )
      base.searchParams.set('width', String(width))
      return base.toString()
    } catch {
      return url
    }
  }

  _createCollectionSearchCard(product) {
    const card = document.createElement('nova-product-card')
    const variant = product.variants?.[0]
    const imageSrc =
      product.images?.[0]?.src ||
      (typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image?.src) ||
      ''
    const sizedImageSrc = imageSrc
      ? this._shopifyImageUrl(imageSrc, COLLECTION_CARD_IMAGE_MAX_WIDTH)
      : ''

    card.id = String(product.id)
    card.setAttribute('handle', product.handle || '')
    card.setAttribute('url', `/products/${product.handle || ''}`)
    card.setAttribute('available', String(Boolean(variant?.available)))
    card.setAttribute('title', product.title || '')
    card.setAttribute('vendor', this._normalizeVendor(product.vendor))
    card.setAttribute(
      'price',
      this._priceToCentsAttr(variant?.price ?? product.price),
    )
    if (variant?.sku) card.setAttribute('sku', variant.sku)
    if (sizedImageSrc) card.setAttribute('has-primary-image', sizedImageSrc)

    const compareAt = variant?.compare_at_price
    const priceNum = this._productPriceNumber(product)
    const compareNum = Number.parseFloat(String(compareAt || '').replace(/,/g, ''))
    if (compareAt && compareNum > priceNum) {
      card.setAttribute('compare-at-price', this._priceToCentsAttr(compareAt))
      card.setAttribute(
        'savings-percent',
        String(Math.round(((compareNum - priceNum) * 100) / compareNum)),
      )
    }

    if (imageSrc) {
      const media = document.createElement('div')
      media.slot = 'primary-image'
      media.className = 'img'
      const img = document.createElement('img')
      img.src = sizedImageSrc
      img.srcset = COLLECTION_CARD_IMAGE_WIDTHS.map(
        (width) => `${this._shopifyImageUrl(imageSrc, width)} ${width}w`,
      ).join(', ')
      img.sizes = COLLECTION_CARD_IMAGE_SIZES
      img.alt = product.title || ''
      img.loading = 'lazy'
      img.width = COLLECTION_CARD_IMAGE_MAX_WIDTH
      img.height = COLLECTION_CARD_IMAGE_MAX_WIDTH
      media.appendChild(img)
      card.appendChild(media)
    }

    card.variants = Array.isArray(product.variants) ? product.variants : []
    card.swatches = []
    card.sizes = []

    return card
  }

  _renderCollectionSearchResults(products) {
    const resultsTarget = document.getElementById('js-collection-product-results')
    if (!resultsTarget) return

    let list = resultsTarget.querySelector('.product-card-grid__items')
    const grid = resultsTarget.querySelector('.js-product-grid')

    if (!list) {
      resultsTarget.innerHTML = `
        <div class="product-card-grid js-product-grid">
          <ul class="product-card-grid__items card-grid grid"></ul>
        </div>
      `
      list = resultsTarget.querySelector('.product-card-grid__items')
    }

    list.innerHTML = ''

    if (!products.length) {
      const empty = document.createElement('p')
      empty.textContent =
        document.getElementById('main-collection-product-grid')?.dataset
          ?.noResults || 'No results.'
      list.appendChild(empty)
    } else {
      for (const product of products) {
        const li = document.createElement('li')
        li.appendChild(this._createCollectionSearchCard(product))
        list.appendChild(li)
      }
    }

    const pagination =
      resultsTarget.querySelector('.pagination-wrapper') ||
      document
        .getElementById('main-collection-product-grid')
        ?.querySelector('.pagination-wrapper')
    if (pagination) pagination.remove()

    if (grid) grid.classList.remove('loading')

    document.dispatchEvent(
      new CustomEvent('orp:product-grid-updated', { bubbles: true }),
    )
  }

  _uncheckAllFacetCheckboxes() {
    const forms = document.querySelectorAll(
      '.js-collection-filters__form, .js-mobile-filters-form',
    )
    for (const form of forms) {
      for (const checkbox of form.querySelectorAll(
        'sl-checkbox.js-filter-checkbox',
      )) {
        if (checkbox.checked) checkbox.checked = false
      }
    }
  }

  _clearActiveFacetChips() {
    for (const selector of ['.js-facets--mobile', '.js-facets--desktop']) {
      const el = document.querySelector(selector)
      if (!el) continue
      el.innerHTML = ''
      el.classList.remove('collection-filters__facets--active')
    }

    const filtersRoot = document.getElementById('main-collection-filters')
    filtersRoot?.classList.remove('collection-filters--active')

    this.updateSummaryFilterCount('Clear All')
    for (const marker of document.querySelectorAll('[data-total-filter-count]')) {
      marker.textContent = ''
    }
  }

  _revealCollectionSearchInputs() {
    const cq = new URLSearchParams(window.location.search).get('cq') || ''
    const inputs = document.querySelectorAll('.js-collection-search-input')

    for (const input of inputs) {
      if (cq) input.value = cq
      input.classList.remove('visibility-hidden')
    }
  }

  _handlePriceRangeSubmit(event) {
    const path = event.composedPath ? event.composedPath() : []
    const wrapperFromPath = path.find(
      (node) => node instanceof Element && node.tagName === 'NOVA-FILTERS-WRAPPER',
    )
    const wrapper = wrapperFromPath || event.target?.closest('nova-filters-wrapper')
    if (!wrapper) return
    const isDesktop = wrapper.classList.contains('js-desktop-filters')
    if (isDesktop) {
      const form = wrapper.querySelector('form')
      if (!form) return
      this.debouncedDesktopFilterApply(form)
    }
  }

  _handleCheckboxChange = (event) => {
    const checkbox = event.currentTarget || event.target
    const wrapper = checkbox?.closest('nova-filters-wrapper')
    if (!wrapper) return

    this.updateSummaryFilterCount(checkbox)

    if (checkbox.classList.contains('js-custom-tag-filter')) {
      const form = checkbox.closest('form')
      if (!form) return

      if (wrapper.classList.contains('js-desktop-filters')) {
        this._applyDesktopFiltersFromForm(form)
      }
      return
    }

    const isDesktop = wrapper.classList.contains('js-desktop-filters')
    if (isDesktop) {
      const form = wrapper.querySelector('form')
      if (!form) return
      this.debouncedDesktopFilterApply(form)
    }
  }

  _applyDesktopFiltersFromForm(form) {
    if (!form) return
    this._clearCollectionSearchInputs()
    const searchParams = this._buildSearchParamsFromForm(form)
    const params = this._ensureCollectionQueryParam(searchParams)
    params.delete('cq')
    const pathname = this._getCollectionTagPath(form)
    this.renderPage(params.toString(), null, true, pathname)
  }

  _buildSearchParamsFromForm(form) {
    const formData = new FormData(form)
    const sortInput = form.querySelector('.js-sort-input')

    if (sortInput && !formData.has('sort_by') && sortInput.value) {
      formData.append('sort_by', sortInput.value)
    }

    const combobox = form.querySelector('nova-combobox')
    let comboboxValue = []

    if (combobox?.shadowRoot) {
      const checkedItems = combobox.shadowRoot.querySelectorAll(
        'sl-menu-item[checked]',
      )
      comboboxValue = Array.from(checkedItems).map((item) => item.value)
    }

    if (comboboxValue.length === 1 && comboboxValue[0] === 'Universal') {
      comboboxValue = []
    }

    const schemaTagGroupValues = this._getSchemaTagGroupValues(form)
    return this._getSearchParams(
      formData,
      comboboxValue,
      schemaTagGroupValues,
      form,
    )
  }

  _getCollectionTagPath(form) {
    const { path: collectionUrl } = this._getCollectionMeta()
    if (!collectionUrl || !form) {
      return this._splitPathAndSearch(window.location.pathname).path
    }

    const handles = []
    for (const checkbox of form.querySelectorAll('.js-custom-tag-filter')) {
      if (!checkbox.checked) continue
      const handle = checkbox.dataset.tagHandle
      if (handle && !handles.includes(handle)) handles.push(handle)
    }

    return handles.length > 0
      ? `${collectionUrl}/${handles.join('+')}`
      : collectionUrl
  }

  _getActiveCustomTagFilterForm() {
    return (
      document.querySelector('.js-desktop-filters .js-collection-filters__form') ||
      document.querySelector('.js-collection-filters__form') ||
      document.querySelector('.js-mobile-filters-form')
    )
  }

  _getCustomTagHandlesFromPathname(pathname = window.location.pathname) {
    const { path: collectionUrl } = this._getCollectionMeta()
    if (!collectionUrl) return []

    const { path } = this._splitPathAndSearch(pathname)
    const prefix = `${collectionUrl.replace(/\/$/, '')}/`
    if (!path.startsWith(prefix)) return []

    return path
      .slice(prefix.length)
      .split('+')
      .map((handle) => handle.trim())
      .filter(Boolean)
  }

  _findCustomTagFilterCheckbox(handle) {
    return document.querySelector(
      `.js-custom-tag-filter[data-tag-handle="${CSS.escape(handle)}"]`,
    )
  }

  _getActiveCustomTagGroups(form, pathname = window.location.pathname) {
    const groups = new Map()
    const handles = this._getCustomTagHandlesFromPathname(pathname)

    for (const handle of handles) {
      const checkbox =
        form?.querySelector(
          `.js-custom-tag-filter[data-tag-handle="${CSS.escape(handle)}"]`,
        ) || this._findCustomTagFilterCheckbox(handle)
      const group = checkbox?.dataset?.optionType || 'Tag'
      const tagValue = checkbox?.dataset?.tagValue
      if (!tagValue) continue

      if (!groups.has(group)) groups.set(group, [])
      const groupTags = groups.get(group)
      if (!groupTags.includes(tagValue)) groupTags.push(tagValue)
    }

    return groups
  }

  _shouldUseCustomTagOrFiltering(form, pathname = window.location.pathname) {
    const groups = this._getActiveCustomTagGroups(form, pathname)
    for (const groupTags of groups.values()) {
      if (groupTags.length > 1) return true
    }
    return false
  }

  _filterProductsByCustomTagGroups(products, groups) {
    if (!groups?.size) return products

    return products.filter((product) => {
      const productTags = (Array.isArray(product.tags) ? product.tags : []).map(
        (tag) => String(tag).toLowerCase(),
      )

      for (const groupTags of groups.values()) {
        const groupMatch = groupTags.some((tag) =>
          productTags.includes(String(tag).toLowerCase()),
        )
        if (!groupMatch) return false
      }

      return true
    })
  }

  _initCustomTagOrFilteringFromPage() {
    if (this.templateType !== 'collection') return

    const cq = new URLSearchParams(window.location.search).get('cq')?.trim()
    if (cq) return

    const form = this._getActiveCustomTagFilterForm()
    const pathname = window.location.pathname
    if (!this._shouldUseCustomTagOrFiltering(form, pathname)) return

    const params = new URLSearchParams(window.location.search)
    params.delete('page')
    params.delete('cq')

    this._renderPageWithCustomTagOrFiltering(
      params.toString(),
      null,
      false,
      pathname,
      form,
    )
  }

  async _renderPageWithCustomTagOrFiltering(
    searchParams,
    event,
    updateURLHash = true,
    pathname = window.location.pathname,
    form = null,
  ) {
    if (!this.isConnected) return

    const requestToken = ++this.renderRequestToken
    this.setLoadingState(true)

    const collectionPath = this._getCollectionProductsBasePath()
    if (!collectionPath) {
      this.setLoadingState(false)
      return
    }

    if (this.pendingController) {
      this.pendingController.abort()
    }
    this.pendingController = new AbortController()
    const { signal } = this.pendingController

    try {
      const { path } = this._splitPathAndSearch(pathname)
      const urlParams = this._ensureCollectionQueryParam(
        new URLSearchParams(searchParams),
      )
      urlParams.delete('page')
      urlParams.delete('cq')

      if (
        this.templateType === 'search' &&
        this.queryWord &&
        !urlParams.has('q')
      ) {
        urlParams.set('q', this.queryWord)
      }

      const sectionsArray = this.getSections().filter(
        (section) => section.id !== 'main-collection-product-grid',
      )

      for (const section of sectionsArray) {
        urlParams.append('sections[]', section.section)
      }

      const sectionUrl = `${path}?${urlParams.toString()}`.replace(
        /\/\//g,
        '/',
      )

      if (updateURLHash) {
        const hashParams = this._ensureCollectionQueryParam(
          new URLSearchParams(searchParams),
        )
        hashParams.delete('page')
        hashParams.delete('cq')
        this.updateURLHash(hashParams.toString(), path)
      }

      const [sectionResponse, products] = await Promise.all([
        fetch(sectionUrl, { signal }).then((response) => response.json()),
        this._fetchCollectionProducts(collectionPath),
      ])

      if (requestToken !== this.renderRequestToken) return
      if (!this.isConnected) return

      const html = Object.values(sectionResponse).join('')
      this.filterData = [...this.filterData, { html, url: sectionUrl }]
      this.renderBanner(html)
      this.renderFilters(html)

      const activeForm = form || this._getActiveCustomTagFilterForm()
      const groups = this._getActiveCustomTagGroups(activeForm, path)
      let matched = this._filterProductsByCustomTagGroups(products, groups)
      matched = this._sortCollectionProducts(
        matched,
        urlParams.get('sort_by') || '',
      )
      this._renderCollectionSearchResults(matched)
    } catch (error) {
      if (error?.name === 'AbortError') return
      console.error('Custom tag filter render failed:', error)
    } finally {
      if (requestToken === this.renderRequestToken) {
        this.setLoadingState(false)
      }
    }
  }

  _handleComboboxUpdate = (event) => {
    const { param, values } = event.detail
    this._clearCollectionSearchInputs()
    const currentSearchParams = this._ensureCollectionQueryParam(
      new URLSearchParams(window.location.search),
    )

    currentSearchParams.delete(`${param}`)
    currentSearchParams.delete('cq')
    for (const item of values) {
      currentSearchParams.append(`${param}`, item.value)
    }

    this.debouncedRenderPage(currentSearchParams.toString(), event)
    this._closeMobileFilters()
  }

  updateSummaryFilterCount(currentFilter) {
    if (currentFilter === 'Clear All') {
      const $filterCountElements = document.querySelectorAll(
        '[data-filter-count]',
      )
      for (const $filterCountElement of $filterCountElements) {
        $filterCountElement.dataset.filterCount = 0
        $filterCountElement.innerHTML = ''
      }
      return
    }

    const optionType = currentFilter.dataset.optionType
    const $filterCountElements = document.querySelectorAll(
      `[data-filter-count][data-filter-option='${optionType}']`,
    )
    if (!$filterCountElements.length) return
    const filterCountValue = Number.parseInt(
      $filterCountElements[0].dataset.filterCount,
    )
    let newCountValue

    if (currentFilter.checked) {
      newCountValue = filterCountValue + 1
    } else {
      newCountValue = filterCountValue - 1
    }

    for (const $element of $filterCountElements) {
      $element.dataset.filterCount = newCountValue
      $element.innerHTML = newCountValue > 0 ? ` (${newCountValue})` : ''
    }
  }

  _closeMobileFilters = () => {
    this.$mobileFiltersDrawer?.hide()
  }

  _closeDetails(event) {
    const $detailsElements = this.querySelectorAll('.js-details-dropdown')
    for (const $element of $detailsElements) {
      if (!$element.contains(event.target)) {
        $element.open = false
      }
    }
  }

  _getSchemaTagGroupValues(form) {
    const selectedTags = []
    const schemaTagGroups = form.querySelectorAll('.js-schema-tag-group')

    for (const groupCheckbox of schemaTagGroups) {
      if (!groupCheckbox.checked) continue

      const tagsCsv = groupCheckbox.dataset.schemaTagCsv
      if (!tagsCsv) continue

      const tags = tagsCsv
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)

      for (const tag of tags) {
        selectedTags.push(tag)
      }
    }

    return selectedTags
  }

  _getSearchParams = (
    formData,
    comboboxValue,
    schemaTagGroupValues = [],
    form = null,
  ) => {
    const filteredFormData = new FormData()

    for (const [key, value] of formData) {
      if (value === '') continue
      if (key.startsWith('filter.p.')) continue
      if (key === 'cq') continue
      filteredFormData.append(key, value)
    }

    if (form) {
      for (const checkbox of form.querySelectorAll(
        'sl-checkbox.js-filter-checkbox[name]',
      )) {
        if (!checkbox.checked) continue

        const name = checkbox.getAttribute('name')
        const value = checkbox.value ?? checkbox.getAttribute('value')
        if (!name || value == null || value === '') continue

        filteredFormData.append(name, value)
      }
    }

    for (const value of comboboxValue) {
      if (value !== '') {
        filteredFormData.append('filter.p.m.custom.fitment', value)
      }
    }

    const uniqueSchemaTags = Array.from(new Set(schemaTagGroupValues))
    for (const tagValue of uniqueSchemaTags) {
      if (tagValue !== '') {
        filteredFormData.append('filter.p.tag', tagValue)
      }
    }

    return new URLSearchParams(filteredFormData).toString()
  }

  onSubmitHandler = (event) => {
    event.preventDefault()
    const form = event.target.closest('form')
    if (!form) return

    const cqInput = form.querySelector('.js-collection-search-input')
    const cqValue = (cqInput?.value || '').trim()
    if (cqValue && event.target?.classList?.contains('js-sort-input')) {
      this._runCollectionSearch(cqValue, true)
      return
    }

    this._clearCollectionSearchInputs()
    const searchParams = this._buildSearchParamsFromForm(form)
    const params = this._ensureCollectionQueryParam(searchParams)
    params.delete('cq')
    const pathname = this._getCollectionTagPath(form)
    this.renderPage(params.toString(), event, true, pathname)
    this._closeMobileFilters()
    this.updateMobileCount(event.target)
  }

  updateMobileCount(target) {
    const filterContainer = target.closest('.mobile-filters__content')
    if (!filterContainer) return
    const filters = filterContainer.querySelectorAll('sl-details')
    for (const filter of filters) {
      const filterCount = filter.querySelector('.js-filter-count')
      if (filterCount) {
        const filterCheckboxes = filter.querySelectorAll('sl-checkbox')
        let count = 0
        for (const checkbox of filterCheckboxes) {
          if (checkbox.checked) {
            count++
          }
        }
        filterCount.innerHTML = count > 0 ? ` (${count})` : ''
        filterCount.dataset.filterCount = count
      }
    }
  }

  // Clears a specific filter value or all filters from the collection form
  clearFormValue(target) {
    const filterValue = target.closest(".js-facet-remove")?.dataset.filterValue;
    if (!filterValue) return;

    const formSelector =
      ".js-collection-filters__form, .js-mobile-filters-form";
    const forms = document.querySelectorAll(formSelector);
    if (!forms.length) return;

    this.#resetFilterElements(forms, filterValue);
    this.#updateFilterUI(forms[0], filterValue);
  }

  #resetFilterElements(forms, targetValue) {
    const isClearAll = targetValue === "Clear All";

    forms.forEach((form) => {
      if (isClearAll) {

        form
          .querySelectorAll("sl-checkbox[checked], sl-menu-item[checked]")
          .forEach((item) => item.removeAttribute("checked"));

        form
          .querySelectorAll('input[type="checkbox"]')
          .forEach((input) => (input.checked = false));

        form
          .querySelectorAll(
            'input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]',
          )
          .forEach((input) => {
            input.value = ''
          })
      } else {

        // NOTE: Reset only the targeted filter
        const selector = `sl-checkbox[value="${targetValue}"], sl-menu-item[value="${targetValue}"]`;
        const filterElement = form.querySelector(selector);
        const novaCombobox = form.querySelector("nova-combobox");
        if (filterElement) {
          filterElement.removeAttribute("checked");
        }
        if (novaCombobox) {
          const selectedItem = novaCombobox.shadowRoot.querySelector(selector);
          if (selectedItem) {
            selectedItem.removeAttribute("checked");
          }

          const restSelectedItems = novaCombobox.shadowRoot.querySelectorAll("sl-menu-item[checked]");
          if (restSelectedItems.length === 1 && restSelectedItems[0].value === 'Universal') {
            restSelectedItems[0].removeAttribute("checked");
          }
        }

        const schemaGroupCheckboxes = form.querySelectorAll('.js-schema-tag-group');
        for (const schemaGroupCheckbox of schemaGroupCheckboxes) {
          const tagsCsv = schemaGroupCheckbox.dataset.schemaTagCsv || '';
          const mappedTags = tagsCsv
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean);
          const optionType = schemaGroupCheckbox.dataset.optionType || '';

          if (mappedTags.includes(targetValue) || optionType === targetValue) {
            schemaGroupCheckbox.removeAttribute("checked");
            schemaGroupCheckbox.checked = false;

            for (const tag of mappedTags) {
              const nativeTagCheckbox = form.querySelector(
                `sl-checkbox[name="filter.p.tag"][value="${CSS.escape(tag)}"]`,
              );
              if (nativeTagCheckbox) {
                nativeTagCheckbox.removeAttribute("checked");
                nativeTagCheckbox.checked = false;
              }
            }
          }
        }
      }
    });
  }

  #updateFilterUI(referenceForm, targetValue) {
    const isClearAll = targetValue === "Clear All";

    if (isClearAll) {
      this.updateSummaryFilterCount("Clear All");
    } else {
      const selector = `sl-checkbox[value="${targetValue}"], sl-menu-item[value="${targetValue}"]`;
      const filterElement = referenceForm.querySelector(selector);
      if (filterElement) {
        this.updateSummaryFilterCount(filterElement);
      } else {
        const schemaGroupCheckboxes = referenceForm.querySelectorAll('.js-schema-tag-group');
        for (const schemaGroupCheckbox of schemaGroupCheckboxes) {
          const tagsCsv = schemaGroupCheckbox.dataset.schemaTagCsv || '';
          const mappedTags = tagsCsv
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean);
          const optionType = schemaGroupCheckbox.dataset.optionType || '';

          if (mappedTags.includes(targetValue) || optionType === targetValue) {
            this.updateSummaryFilterCount(schemaGroupCheckbox);
            break;
          }
        }
      }
    }
  }

  _syncCustomTagFiltersToDesktop(parsedDoc = document) {
    const sourceRoot =
      parsedDoc.querySelector('.js-collection-tag-filters-source .js-collection-tag-filters') ||
      parsedDoc.querySelector('#main-collection-filters .js-collection-tag-filters-source .js-collection-tag-filters') ||
      document.querySelector(
        '.js-collection-tag-filters-source .js-collection-tag-filters',
      )

    const desktopTarget = document.querySelector(
      '.js-desktop-filters .js-collection-tag-filters--desktop',
    )
    if (!desktopTarget) {
      this._hideNativeTagFilterIfCustomEnabled(parsedDoc)
      return
    }

    if (!sourceRoot) {
      desktopTarget.innerHTML = ''
      this._hideNativeTagFilterIfCustomEnabled(parsedDoc)
      return
    }

    desktopTarget.innerHTML = sourceRoot.innerHTML

    for (const detail of desktopTarget.querySelectorAll('.js-details-dropdown')) {
      detail.classList.remove('visibility-hidden')
      if (detail.querySelector('sl-checkbox[checked]')) {
        detail.open = true
      }
    }

    this._bindDesktopFilterCheckboxes(desktopTarget)
    this._hideNativeTagFilterIfCustomEnabled(parsedDoc)
  }

  _hideNativeTagFilterIfCustomEnabled(parsedDoc = document) {
    const filtersRoot =
      parsedDoc.getElementById?.('main-collection-filters') ||
      parsedDoc.querySelector?.('#main-collection-filters') ||
      document.getElementById('main-collection-filters')
    const shouldHide = filtersRoot?.dataset?.hideNativeTagFilter === 'true'

    const forms = document.querySelectorAll(
      '.js-desktop-filters .js-collection-filters__form, .js-mobile-filters-form',
    )

    for (const form of forms) {
      const tagCheckboxes = form.querySelectorAll(
        'sl-checkbox[name="filter.p.tag"]',
      )
      const detailsToToggle = new Set()

      for (const checkbox of tagCheckboxes) {
        if (checkbox.closest('.js-collection-tag-filters')) continue
        const details = checkbox.closest('sl-details')
        if (details) detailsToToggle.add(details)
      }

      for (const details of detailsToToggle) {
        if (shouldHide) {
          details.hidden = true
          details.setAttribute('hidden', '')
          details.classList.add('js-native-tag-filter--hidden')
        } else {
          details.hidden = false
          details.removeAttribute('hidden')
          details.classList.remove('js-native-tag-filter--hidden')
        }
      }
    }
  }

  _bindDesktopFilterCheckboxes(root) {
    const desktopForm = document.querySelector(
      '.js-desktop-filters .js-collection-filters__form',
    )
    if (!desktopForm) return

    const scope = root || desktopForm
    const checkboxes = scope.querySelectorAll('.js-filter-checkbox')
    for (const checkbox of checkboxes) {
      checkbox.removeEventListener('sl-change', this._handleCheckboxChange)
      checkbox.addEventListener('sl-change', this._handleCheckboxChange)
    }
  }

  _stripFilterParams(searchParams) {
    const params =
      searchParams instanceof URLSearchParams
        ? new URLSearchParams(searchParams)
        : new URLSearchParams(searchParams || '')
    for (const key of [...params.keys()]) {
      if (key.startsWith('filter.')) params.delete(key)
    }
    params.delete('page')
    params.delete('cq')
    return params
  }

  _clearAllCollectionFilters(event) {
    this.clearFormValue(event.target)

    const form =
      document.querySelector('.js-desktop-filters .js-collection-filters__form') ||
      document.querySelector('.js-collection-filters__form') ||
      document.querySelector('.js-mobile-filters-form')

    const params = this._ensureCollectionQueryParam(
      form
        ? new URLSearchParams(this._buildSearchParamsFromForm(form))
        : new URLSearchParams(window.location.search),
    )
    const cleared = this._stripFilterParams(params)
    const pathname = form
      ? this._getCollectionTagPath(form)
      : this._getCollectionMeta().path ||
      this._splitPathAndSearch(window.location.pathname).path

    this.renderPage(cleared.toString(), event, true, pathname)
  }

  _onActiveFilterClick = (event) => {
    const removeButton = event.target.closest('.js-facet-remove')
    if (!removeButton || !removeButton.dataset.filterValue) return

    event.preventDefault()
    this.toggleActiveFacets()

    if (removeButton.dataset.filterValue === 'Clear All') {
      this._clearAllCollectionFilters(event)
      return
    }

    if (removeButton.classList.contains('js-custom-tag-facet')) {
      const targetUrl = new URL(removeButton.href, window.location.origin)
      const searchParams = this._ensureCollectionQueryParam(
        new URLSearchParams(window.location.search),
      )
      searchParams.delete('page')
      this.renderPage(
        searchParams.toString(),
        event,
        true,
        targetUrl.pathname,
      )
      return
    }

    this.clearFormValue(event.target)

    const form =
      document.querySelector('.js-collection-filters__form') ||
      document.querySelector('.js-mobile-filters-form')

    if (!form) return

    const searchParams = this._ensureCollectionQueryParam(
      this._buildSearchParamsFromForm(form),
    )
    this.debouncedRenderPage(searchParams.toString(), event)
  }

  _onHistoryChange = (event) => {
    const searchParams =
      event.state?.searchParams || window.location.search.slice(1)
    const pathname = event.state?.pathname || window.location.pathname
    const params = new URLSearchParams(searchParams)
    const cq = params.get('cq')?.trim()

    if (cq && this.templateType === 'collection') {
      this._syncCollectionSearchInputs(cq)
      this._runCollectionSearch(cq, false)
      return
    }

    this._clearCollectionSearchInputs()
    this.debouncedRenderPage(searchParams, null, false, pathname)
  }

  toggleActiveFacets(disable = true) {
    const $removeFacets = document.querySelectorAll('.js-facet-remove')
    for (const $element of $removeFacets) {
      $element.classList.toggle('disabled', disable)
    }
  }

  setLoadingState(isLoading) {
    const productGridContainer = document.getElementById(
      'main-collection-product-grid',
    )
    const gridElement = productGridContainer?.querySelector('.js-product-grid')
    const paginationWrapper =
      productGridContainer?.querySelector('.pagination-wrapper')

    if (gridElement) {
      gridElement.classList.toggle('loading', isLoading)
    }

    if (paginationWrapper) {
      const collectionSearchActive = Boolean(this._getCollectionSearchQuery())
      if (collectionSearchActive) {
        paginationWrapper.classList.add('hidden')
      } else {
        paginationWrapper.classList.toggle('hidden', isLoading)
      }
    }
  }

  renderPage(
    searchParams,
    event,
    updateURLHash = true,
    pathname = window.location.pathname,
  ) {
    if (!this.isConnected) return

    const { path, searchParams: pathParams } = this._splitPathAndSearch(pathname)
    const params = this._ensureCollectionQueryParam(
      new URLSearchParams(searchParams),
    )
    for (const [key, value] of pathParams.entries()) {
      if (!params.has(key)) params.append(key, value)
    }

    if (params.get('cq')?.trim()) {
      this._runCollectionSearch(params.get('cq').trim(), updateURLHash)
      return
    }

    const form = this._getActiveCustomTagFilterForm()
    if (
      this.templateType === 'collection' &&
      this._shouldUseCustomTagOrFiltering(form, path)
    ) {
      this._renderPageWithCustomTagOrFiltering(
        params.toString(),
        event,
        updateURLHash,
        path,
        form,
      )
      return
    }

    try {
      const sectionsArray = this.getSections()
      if (!sectionsArray.length) return

      const requestToken = ++this.renderRequestToken
      this.setLoadingState(true)

      const urlParams = new URLSearchParams(params)
      if (this.templateType === 'search' && this.queryWord && !urlParams.has('q')) {
        urlParams.set('q', this.queryWord)
      }

      for (const section of sectionsArray) {
        urlParams.append('sections[]', section.section)
      }

      const url = `${path}?${urlParams.toString()}`
      const normalizedUrl = url.replace(/\/\//g, '/')

      const filterDataUrl = (element) => element.url === normalizedUrl

      const cachedItem = this.filterData.find(filterDataUrl);

      if (updateURLHash) this.updateURLHash(params.toString(), path)

      if (cachedItem) {
        this.renderSectionFromCache(cachedItem)
        this.setLoadingState(false)
      } else {
        this.renderSectionFromFetch(normalizedUrl, requestToken)
      }
    } catch (error) {
      console.error('Error in renderPage:', error)
      this.setLoadingState(false)
    }
  }

  renderSectionFromFetch(url, requestToken) {
    if (this.pendingController) {
      this.pendingController.abort()
    }

    this.pendingController = new AbortController()
    fetch(url, { signal: this.pendingController.signal })
      .then((response) => response.json())
      .then((parsed) => {
        if (requestToken !== this.renderRequestToken) return
        if (!this.isConnected) return
        const html = Object.values(parsed).join('')
        this.filterData = [...this.filterData, { html, url }]
        this.renderBanner(html)
        this.renderFilters(html)
        this.renderProductGrid(html)
      })
      .catch((error) => {
        if (error.name === 'AbortError') return
        console.error('Error fetching data:', error)
      })
      .finally(() => {
        if (requestToken === this.renderRequestToken) {
          this.setLoadingState(false)
        }
      })
  }

  updateInnerHTML(html, elementId) {
    const parsedDoc = new DOMParser().parseFromString(html, 'text/html')
    const sourceElement = parsedDoc.getElementById(elementId)
    if (!sourceElement) return

    const innerHTML = sourceElement.innerHTML
    if (!innerHTML) return

    const targetElement = document.getElementById(elementId)
    if (!targetElement) return

    targetElement.innerHTML = innerHTML
  }

  renderBanner(html) {
    if (this.templateType === 'search') return
    const bannerElement = document.getElementById('main-collection-banner')
    if (!bannerElement) return
    this.updateInnerHTML(html, 'main-collection-banner')
  }

  renderSectionFromCache(filterDataItem, event) {
    if (!filterDataItem) return
    const filterDataUrlCheck = filterDataItem.url.replace(/\/\//g, '/');
    const matchedItem = this.filterData.find(item => item.url === filterDataUrlCheck);
    const html = matchedItem?.html;
    if (!html) return
    this.renderFilters(html)
    this.renderProductGrid(html)
  }

  renderProductGrid(html) {
    const parsedDoc = new DOMParser().parseFromString(html, 'text/html')
    const resultsTarget = document.getElementById('js-collection-product-results')
    const sourceResults = parsedDoc.getElementById('js-collection-product-results')
    const usedPartialUpdate = Boolean(resultsTarget && sourceResults)

    if (usedPartialUpdate) {
      resultsTarget.innerHTML = sourceResults.innerHTML
      this.syncDesktopSidebarFromResponse(parsedDoc)
    } else {
      this.updateInnerHTML(html, 'main-collection-product-grid')
    }

    const filters = document.querySelectorAll(
      '.js-sort-input, .js-details-dropdown, .js-collection-search-input',
    )

    for (const filter of filters) {
      filter.classList.remove('visibility-hidden')
    }

    this._revealCollectionSearchInputs()

    const filterComboboxes = document.querySelectorAll('.js-filter-combobox')

    for (const combobox of filterComboboxes) {
      const wrapper = combobox.closest('div')
      const name = combobox.getAttribute('name')
      const dataTemplate = wrapper?.querySelector(`template[name="${name}"]`)

      if (!dataTemplate) {
        continue
      }

      try {
        const json = dataTemplate.innerHTML.trim()
        const options = JSON.parse(json)
        combobox.options = options
      } catch (error) {
        console.error(`Failed to parse options for "${name}"`, error)
      }
    }

    const productGridRoot =
      document.getElementById('js-collection-product-results') ||
      document.getElementById('main-collection-product-grid')
    const productCards =
      productGridRoot?.querySelectorAll('nova-product-card') ||
      document.querySelectorAll('nova-product-card')

    for (const productCard of productCards) {
      try {
        const swatchValuesTemplate = productGridRoot.querySelector(
          `#collection-grid__swatch-values-json-${productCard.id}`,
        )
        const sizeValuesTemplate = productGridRoot.querySelector(
          `#collection-grid__size-values-json-${productCard.id}`,
        )
        const variantsTemplate = productGridRoot.querySelector(
          `#collection-grid__variants-json-${productCard.id}`,
        )
        const swatchValues = swatchValuesTemplate
          ? JSON.parse(swatchValuesTemplate.content.textContent.trim())
          : []
        const sizeValues = sizeValuesTemplate
          ? JSON.parse(sizeValuesTemplate.content.textContent.trim())
          : []
        const variants = variantsTemplate
          ? JSON.parse(variantsTemplate.content.textContent.trim())
          : []
        productCard.swatches = swatchValues
        productCard.sizes = sizeValues
        productCard.variants = variants
      } catch (error) {
        console.error('Error hydrating product card variants:', error)
      }
    }

    document.dispatchEvent(
      new CustomEvent('orp:product-grid-updated', { bubbles: true }),
    )
  }

  syncDesktopSidebarFromResponse(parsedDoc) {
    const liveForm = document.querySelector(
      '.js-desktop-filters .js-collection-filters__form',
    )
    if (!liveForm) {
      this._syncCustomTagFiltersToDesktop(parsedDoc)
      return
    }

    const sourceForm =
      parsedDoc.querySelector(
        '.js-desktop-filters .js-collection-filters__form',
      ) ||
      parsedDoc.querySelector(
        '#main-collection-product-grid .js-collection-filters__form',
      )

    if (sourceForm) {
      for (const sourceCheckbox of sourceForm.querySelectorAll(
        '.js-filter-checkbox',
      )) {
        const name = sourceCheckbox.getAttribute('name')
        const value = sourceCheckbox.getAttribute('value')
        if (!name || value == null) continue

        const liveCheckbox = liveForm.querySelector(
          `sl-checkbox[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`,
        )
        if (!liveCheckbox) continue

        if (sourceCheckbox.hasAttribute('disabled')) {
          liveCheckbox.setAttribute('disabled', '')
        } else {
          liveCheckbox.removeAttribute('disabled')
        }
      }

      for (const sourceCount of sourceForm.querySelectorAll('.js-filter-count')) {
        const option = sourceCount.dataset.filterOption
        if (!option) continue

        const liveCountElements = document.querySelectorAll(
          `.js-desktop-filters .js-filter-count[data-filter-option="${CSS.escape(option)}"]`,
        )
        for (const liveCount of liveCountElements) {
          liveCount.dataset.filterCount = sourceCount.dataset.filterCount
          liveCount.innerHTML = sourceCount.innerHTML
        }
      }
    }

    this._syncCustomTagFiltersToDesktop(parsedDoc)
  }

  renderFilters(html) {
    this.renderSingleFormFilters(html)
  }

  renderSingleFormFilters(html) {
    this.updateInnerHTML(html, 'main-collection-filters')
    this.renderActiveFacets(html)

    const parsedDoc = new DOMParser().parseFromString(html, 'text/html')
    this._syncCustomTagFiltersToDesktop(parsedDoc)

    const filtersRoot = document.getElementById('main-collection-filters')
    if (filtersRoot) {
      for (const detail of filtersRoot.querySelectorAll(
        '.js-collection-tag-filter.js-details-dropdown',
      )) {
        detail.classList.remove('visibility-hidden')
        if (detail.querySelector('sl-checkbox[checked]')) {
          detail.open = true
        }
      }

      for (const el of filtersRoot.querySelectorAll(
        '.js-sort-input, .js-collection-search-input',
      )) {
        el.classList.remove('visibility-hidden')
      }
    }

    this._revealCollectionSearchInputs()
  }

  _fixDoubleQueryHrefs(root = document) {
    for (const link of root.querySelectorAll('a[href*="?"]')) {
      const href = link.getAttribute('href')
      if (!href) continue
      const firstQ = href.indexOf('?')
      const secondQ = href.indexOf('?', firstQ + 1)
      if (secondQ === -1) continue
      link.setAttribute(
        'href',
        `${href.slice(0, secondQ)}&${href.slice(secondQ + 1)}`,
      )
    }
  }

  renderActiveFacets(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
    const activeFacetElementSelectors = [
      '.js-facets--mobile',
      '.js-facets--desktop',
    ]

    for (const selector of activeFacetElementSelectors) {
      const $activeFacetsElement = parsedHTML.querySelector(selector)
      if (!$activeFacetsElement) continue

      this._fixDoubleQueryHrefs($activeFacetsElement)

      const existingElement = document.querySelector(selector)
      if (existingElement) {
        existingElement.replaceWith($activeFacetsElement)
      }
    }

    this._fixDoubleQueryHrefs(document)
    this.bindActiveFacetButtonEvents()
    this.toggleActiveFacets(false)
  }

  bindActiveFacetButtonEvents() {
    const $removeFacets = document.querySelectorAll('.js-facet-remove')
    for (const $element of $removeFacets) {
      $element.addEventListener('click', this._onActiveFilterClick, {
        once: true,
      })
    }
  }

  updateURLHash(searchParams, pathname = window.location.pathname) {
    const { path, searchParams: pathParams } = this._splitPathAndSearch(pathname)
    const params = this._ensureCollectionQueryParam(
      new URLSearchParams(searchParams || ''),
    )
    for (const [key, value] of pathParams.entries()) {
      if (!params.has(key)) params.append(key, value)
    }

    let parameters = params.toString()

    if (
      this.templateType === 'search' &&
      this.queryWord &&
      !params.has('q')
    ) {
      parameters = `q=${encodeURIComponent(this.queryWord)}${parameters ? `&${parameters}` : ''
        }`
    }

    const newUrl = `${path}${parameters ? `?${parameters}` : ''}`
    const newUrlCheck = newUrl.replace(/\/\//g, '/')

    history.pushState(
      { searchParams: parameters, pathname: path },
      '',
      newUrlCheck,
    )
  }

  uniqueSections(sections) {
    const seen = new Set()
    return sections.filter((section) => {
      if (seen.has(section.section)) return false
      seen.add(section.section)
      return true
    })
  }

  getSections() {
    if (this.templateType === 'search') {
      const filtersEl = document.getElementById('main-collection-filters')
      const gridEl = document.getElementById('main-collection-product-grid')
      const sections = []

      if (filtersEl?.dataset?.id) {
        sections.push({
          id: 'main-collection-filters',
          section: filtersEl.dataset.id,
        })
      }

      if (gridEl?.dataset?.id) {
        sections.push({
          id: 'main-collection-product-grid',
          section: gridEl.dataset.id,
        })
      }

      return this.uniqueSections(sections)
    }

    const sections = []
    const bannerEl = document.getElementById('main-collection-banner')
    const filtersEl = document.getElementById('main-collection-filters')
    const gridEl = document.getElementById('main-collection-product-grid')

    if (bannerEl?.dataset?.id) {
      sections.push({
        id: 'main-collection-banner',
        section: bannerEl.dataset.id,
      })
    }

    if (filtersEl?.dataset?.id) {
      sections.push({
        id: 'main-collection-filters',
        section: filtersEl.dataset.id,
      })
    }

    if (gridEl?.dataset?.id) {
      sections.push({
        id: 'main-collection-product-grid',
        section: gridEl.dataset.id,
      })
    }

    return this.uniqueSections(sections)
  }
}

customElements.define('nova-filters-wrapper', NovaFiltersWrapper)
